import { Badge, Button, Grid, Icon, Message } from "@intility/bifrost-react";
import type { Columns } from "@intility/gjallarbru";
import { useMemo } from "react";
import { useNavigate } from "react-router";
import { useClients } from "../../api/clients";
import { usePlatforms } from "../../api/platforms";
import type { ClientListItem, PlatformListItem } from "../../api/types";
import { PageLayout } from "../../components/layout/PageLayout";
import { RoleBadge } from "../../components/RoleBadge";
import { PlatformsTable } from "../../components/tables/PlatformsTable";
import { formatDate } from "../../lib/clientTypes";

// The registry page's own columns after the shared Name, Description and
// Clients.
const columnsAfterClients: Columns<PlatformListItem> = [
  {
    accessorKey: "userRole",
    Cell: ({ row }: { row: { original: PlatformListItem } }) => (
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <RoleBadge role={row.original.userRole} />
        {row.original.provisioningStatus === "failed" && (
          <Badge state="alert">Provisioning failed</Badge>
        )}
        {row.original.provisioningStatus === "provisioning" && (
          <Badge state="warning">Provisioning</Badge>
        )}
      </div>
    ),
    header: "Role",
    // gjallarbru's multi-select filter; its options are the roles present.
    filterVariant: "multi-select",
    size: 200,
  },
  {
    accessorKey: "createdAt",
    Cell: ({ row }: { row: { original: PlatformListItem } }) => (
      <span>{formatDate(row.original.createdAt)}</span>
    ),
    header: "Created",
    size: 140,
  },
];

export default function Platforms() {
  const { data: platforms, isLoading, error } = usePlatforms();
  const { data: clients } = useClients();
  const navigate = useNavigate();

  // The clients shown in the table's Clients column come from the same list
  // the Clients page uses. Every item carries its `platformId`, so no
  // per-platform request is needed.
  const clientsByPlatform = useMemo(() => {
    const byPlatform = new Map<string, ClientListItem[]>();
    for (const client of clients ?? []) {
      if (!client.platformId) continue;
      const existing = byPlatform.get(client.platformId);
      if (existing) existing.push(client);
      else byPlatform.set(client.platformId, [client]);
    }
    return byPlatform;
  }, [clients]);

  if (isLoading) {
    return (
      <div className="bf-content bfl-page-padding">
        <Grid
          gap={16}
          style={{
            minHeight: "200px",
            placeContent: "center",
            textAlign: "center",
          }}
        >
          <Icon.Spinner size={32} />
          <p className="bf-p">Loading platforms...</p>
        </Grid>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bf-content bfl-page-padding">
        <Message state="warning" header="Failed to load platforms">
          {error instanceof Error
            ? error.message
            : "An unexpected error occurred."}
        </Message>
      </div>
    );
  }

  if (!platforms || platforms.length === 0) {
    return (
      <div className="bf-content bfl-page-padding">
        <Grid
          gap={24}
          style={{
            maxWidth: "480px",
            margin: "80px auto",
            textAlign: "center",
          }}
        >
          <h2 className="bf-h2">Your Platforms</h2>
          <p className="bf-p">
            You do not have a registered platform yet. A platform groups the
            clients your service needs. Create one to start.
          </p>
          <div
            style={{ display: "flex", gap: "12px", justifyContent: "center" }}
          >
            <Button onClick={() => navigate("/platforms/new")}>
              Register a platform
            </Button>
          </div>
        </Grid>
      </div>
    );
  }

  return (
    <PageLayout
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Platforms" }]}
      title="Platforms"
      description="A platform groups its clients and has one blueprint. It also has the authorization and integration guides for its clients."
      actions={
        <Button variant="filled" onClick={() => navigate("/platforms/new")}>
          Register platform
        </Button>
      }
    >
      <PlatformsTable
        platforms={platforms}
        clientsByPlatform={clientsByPlatform}
        columnsAfter={columnsAfterClients}
        name="platforms"
      />
    </PageLayout>
  );
}
